/**
 * Rollout flags for whole product surfaces. A flag answers "does this
 * surface exist for you yet"; it is NOT a plan gate, which answers "does
 * your tier include it" and sells the upgrade on refusal. The flag is
 * checked first, the plan gate second, and rollout state never goes into
 * FEATURE_REQUIREMENTS.
 *
 * Each key is a PostHog feature flag, released on the `beta_access` person
 * property that identify() already sets. A flag nobody has created in the
 * project reads as OFF once flags load: a hidden surface is the safe
 * failure for a rollout gate.
 *
 * Plan: docs/superpowers/plans/2026-09-09-posthog-observability-plan.md, task 7.
 */
/**
 * These strings must match the flag keys in the PostHog project EXACTLY. A key
 * PostHog does not know evaluates to undefined, which this module treats as
 * off, so a typo hides the surface silently and looks identical to a rollout
 * that has not started. That is what happened to the Backtester and Deep
 * Research icons: the code asked for `surface-*` while the project defines
 * `*-release`, so both were invisible however the flags were set.
 */
import { debugToolsEnabled } from '@/ett-debug-tools/debug-enabled'

export const FLAGS = {
  BACKTESTER: 'backtester-release',
  DEEP_RESEARCH: 'deepresearch-release',
  NOTEBOOK: 'notebook-release',
  /** The pinned Home view on the dashboard (market-overwatch/home-layout). */
  HOME: 'home-release',
  /** The Playbook console: pre-built plays over the history engine. */
  PLAYBOOK: 'playbook-release',
  /**
   * The Journal: the trader's real trades, paired from fills and joined to plans,
   * History and backtests (spec docs/superpowers/specs/2026-10-16-journal-design.md).
   * Chris's user only at first.
   */
  JOURNAL: 'journal-release',
  /**
   * The backtest run report's Robustness and Sizing pages. The designs are not
   * ready yet (Chris, 2026-11-01), so Chris's user only at first.
   */
  BACKTEST_SIZING_ROBUSTNESS: 'backtest-sizing-robustness-release',
  /** Similar Patterns (shape-similarity search). Internal cohort until it is finished. */
  SIMILAR_PATTERNS: 'similarpatterns-release',
  /** The University's Learn to Trade academy: courses, S/R challenges, mastery checks. */
  LEARN_TO_TRADE: 'learntotrade-release',
  /**
   * EdgeScript: the chart's script editor, dock and picker, script indicators, code
   * mode on Build, and the companion's script tools. Checked INSIDE the build gate
   * (`features/edgescript/flags.ts`), never instead of it.
   */
  EDGESCRIPT: 'edgescript-release',
  /**
   * The companion draws on charts: draw_on_chart, read_chart, clear_ai_drawings,
   * set_timeframe, configure_indicator and remove_indicator, plus draw_from_script when EdgeScript is
   * also on (features/charts/companion/).
   */
  AI_CHART_DRAWING: 'aichartdrawing-release',
  /**
   * Scanner minute snapshot: a New York time field beside the header calendar's
   * past date, so the scan reads as of that minute (`historical_time`).
   */
  SCANNER_MINUTE_SNAPSHOT: 'scanner-minute-snapshot-release',
  /** Price Alerts: the panel and its fired/adjusted toasts. Checked INSIDE the realtime build gate. */
  PRICE_ALERTS: 'pricealerts-release',
  /** Alert Windows: the panel and its match toasts. Checked INSIDE the realtime build gate. */
  ALERT_WINDOWS: 'alertwindows-release',
} as const

export type FlagKey = (typeof FLAGS)[keyof typeof FLAGS]

/**
 * Dev-tool flags: internal tooling surfaces, controlled by PostHog in every
 * environment (prod included). Unlike FLAGS they are OFF whenever PostHog
 * cannot say "on" (not configured, loading, unreachable, flag missing), and
 * the `ett:flags` override reaches them only under the Vite dev server. Read
 * them through `dev-flags.ts` (`useDevFlag` / `isDevFlagOn`), never through
 * `useFeatureFlag`, whose "not configured = on" would turn them on locally.
 *
 * Plan: docs/superpowers/plans/2026-10-19-scanner-dev-flags-and-parity-plan.md.
 */
export const DEV_FLAGS = {
  /** The scanner's Context tab data-source switch (real-time scanner views). */
  SCANNER_REALTIME: 'devtools-scanner-realtime',
  /** The scanner parity page (`/dev/scanner-parity`) and its Context tab button. */
  SCANNER_PARITY: 'devtools-scanner-parity',
  /** The onboarding survey preview (`/dashboard?onboarding=preview`): shows the result, saves nothing. */
  ONBOARDING_PREVIEW: 'devtools-onboarding-preview',
} as const

export type DevFlagKey = (typeof DEV_FLAGS)[keyof typeof DEV_FLAGS]

/**
 * Preview a flag as off (or on) for the current tab, in dev only, without a
 * PostHog round trip. Mirrors the `ett:plan-tier` override.
 *
 *   sessionStorage.setItem('ett:flags', JSON.stringify({ 'backtester-release': false }))
 *   location.reload()
 *
 * Clear with `sessionStorage.removeItem('ett:flags'); location.reload()`.
 */
export const DEV_FLAG_OVERRIDE_KEY = 'ett:flags'

/**
 * The overridden value, or null when there is none. `enabled` is a parameter so
 * the guard is testable.
 *
 * Gated on `debugToolsEnabled` rather than `import.meta.env.DEV`, for the same
 * reason as the plan override next door: the DEV flag is false in every build,
 * which made this silently inert on staging, the one deployed environment where
 * previewing a rollout gate is the whole point. Production folds the flag to a
 * literal false and drops the path.
 */
export function readDevFlagOverride(
  flag: string,
  storage: Pick<Storage, 'getItem'> | null | undefined = typeof sessionStorage !== 'undefined'
    ? sessionStorage
    : null,
  enabled: boolean = debugToolsEnabled,
): boolean | null {
  if (!enabled || !storage) return null
  try {
    const raw = storage.getItem(DEV_FLAG_OVERRIDE_KEY)
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (parsed == null || typeof parsed !== 'object') return null
    const v = (parsed as Record<string, unknown>)[flag]
    return typeof v === 'boolean' ? v : null
  } catch {
    return null
  }
}
